/** 一日のまとめのRepository。1日につき1件。 */

import { getDb, type DailyRecord } from './schema';
import { enqueue } from './outbox';
import { bumpData } from './events';
import { getDeviceId } from '../domain/ids';
import { nowIsoUtc } from '../lib/datetime';
import { buildSearchText } from '../lib/text';
import { isDailyEmpty, SCHEMA_VERSION, type DailySummary } from '../domain/types';

function toRecord(d: DailySummary, dirty: boolean, etag: string | null): DailyRecord {
  return {
    ...d,
    monthKey: d.date.slice(0, 7),
    searchText: buildSearchText(d.events, `${d.good}\n${d.action}`),
    _dirty: dirty,
    _etag: etag,
  };
}

export function emptyDaily(date: string): DailySummary {
  const now = nowIsoUtc();
  return {
    schemaVersion: SCHEMA_VERSION,
    date,
    createdAt: now,
    updatedAt: now,
    rev: 0,
    deviceId: getDeviceId(),
    events: '',
    good: '',
    action: '',
  };
}

export async function getDaily(date: string): Promise<DailyRecord | undefined> {
  const db = await getDb();
  return db.get('dailies', date);
}

export async function listDailiesByMonth(month: string): Promise<DailyRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('dailies', 'by_month', month);
  return all.sort((a, b) => (a.date < b.date ? -1 : 1));
}

export async function listDailiesByRange(from: string, to: string): Promise<DailyRecord[]> {
  const db = await getDb();
  const all = await db.getAll('dailies');
  return all.filter((d) => d.date >= from && d.date <= to).sort((a, b) => (a.date < b.date ? -1 : 1));
}

export async function listAllDailies(): Promise<DailyRecord[]> {
  const db = await getDb();
  return (await db.getAll('dailies')).sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** 3項目すべてが空なら削除として扱う。 */
export async function saveDaily(d: DailySummary): Promise<void> {
  const db = await getDb();
  const existing = await db.get('dailies', d.date);

  if (isDailyEmpty(d)) {
    if (existing) await deleteDaily(d.date);
    return;
  }

  const next: DailySummary = {
    ...d,
    schemaVersion: SCHEMA_VERSION,
    deviceId: getDeviceId(),
    updatedAt: nowIsoUtc(),
    rev: (existing?.rev ?? 0) + 1,
    createdAt: existing?.createdAt ?? d.createdAt,
  };
  await db.put('dailies', toRecord(next, true, existing?._etag ?? null));
  await enqueue({
    type: 'daily.upsert',
    targetKey: `daily:${d.date}`,
    dateKey: d.date,
    month: next.date.slice(0, 7),
  });
  bumpData();
}

export async function deleteDaily(date: string): Promise<void> {
  const db = await getDb();
  const rec = await db.get('dailies', date);
  if (!rec) return;
  await db.delete('dailies', date);
  await enqueue({
    type: 'daily.delete',
    targetKey: `daily:${date}`,
    dateKey: date,
    month: rec.monthKey,
  });
  bumpData();
}

export async function putDailyFromRemote(d: DailySummary, etag: string | null): Promise<void> {
  const db = await getDb();
  await db.put('dailies', toRecord(d, false, etag));
}
